<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import type { GeoMap, PostConnectionObject, PostMarkerObject } from '@@/bits/map'
import { Geocoder, GeocoderStatus, getBoundsZoomLevel, NARNIA_LATITUDE, NARNIA_LONGITUDE } from '@@/bits/map'
import { defineAsyncComponent } from '@@/bits/vue'
import { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import type { Cid, Id, Post, PostAttributes } from '@@/types'
import BaseIcon from '@@/vuecomponents/base_icon.vue'
import SurfaceMapGoogle from '@@/vuecomponents/surface_map_google.vue'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfaceMapHeaderAndPostListPanel from '@@/vuecomponents/SurfaceMapHeaderAndPostListPanel.vue'
import SurfaceMapPostListHorizontal from '@@/vuecomponents/SurfaceMapPostListHorizontal.vue'
import { useDragAndDropToCreatePost } from '@@/vuecomposables/drag_and_drop_to_create_post'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const SurfaceSearchBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSearchBar.vue'))
const SurfaceMapPlacedPinOverlay = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceMapPlacedPinOverlay.vue'))
const SurfaceMapLeaflet = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceMapLeaflet.vue'))
const SurfacePadletLogoInScreenshotMode = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePadletLogoInScreenshotMode.vue'),
)

interface NewPost {
  attributes: PostAttributes
}

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfaceMapStore = useSurfaceMapStore()
const surfacePostsStore = useSurfacePostsStore()

const { enableMapFittingMarkersBounds, setMapBeingUsed, toggleMapPreviewPanel } = surfaceMapStore
const { createPostImmediately, saveContent, resetMostRecentlyChangedGeolocationPostCid } = surfacePostsStore
const { uploadFileToPickerBox } = useSurfaceAttachmentsStore()
const { startNewSubmissionRequest } = useSurfaceShareLinksStore()
const { hideContentPickerPanel } = useContentPickerStore()

const { map, isPreviewPanelShown, isPlacingPin, postListPosition } = storeToRefs(surfaceMapStore)
const {
  isNativeTenant,
  isSubmissionRequest,
  isDesktopLayout,
  isMobileLayout,
  isScreenshotMode,
  isReadOnly,
  isEmbedded,
  xHeader,
  backgroundColor,
  colorScheme,
  mapThemeId,
  xLogoInScreenshotMode,
} = storeToRefs(useSurfaceStore())
const { currentCountry } = storeToRefs(useSurfaceStartingStateStore())
const { loginUrl, signupUrl } = storeToRefs(useSurfaceGuestStore())
const { currentPosts, hasFetchedPosts, postEntitiesByCid, postBeingEditedCid, mostRecentlyChangedGeolocationPostCid } =
  storeToRefs(surfacePostsStore)
const { connections } = storeToRefs(useSurfacePostConnectionStore())
const { canIPost, amIRegistered } = storeToRefs(useSurfacePermissionsStore())

const onMapInitialized = (map: GeoMap) => {
  setMapBeingUsed({ map })
}

const postsWithRenderingLocations = computed(
  (): (Omit<Post, 'location_point'> & Required<Pick<Post, 'location_point'>>)[] => {
    if (!hasFetchedPosts.value) {
      return []
    }
    const locationlessPosts: Cid[] = []
    currentPosts.value.forEach((p: Post) => {
      if (!p.location_point || !p.location_point.longitude || !p.location_point.latitude) {
        locationlessPosts.push(p.cid)
      }
    })
    /**
     * * Antarctica:
     * * Latitude from -75 -> -90. 82.5 is the center of the span. At 15 markers, we add a different row:
     *   -82.5 is the center latitude of Antartica
     *   `- locationlessPosts.length / 15` will start the rows a bit lower to have space for new row
     *   `+ 3 * (x % (1 + locationlessPosts.length / 15))` will move the marker a bit above to distribute
     * the marker upwards from the starting location. Modular will repeat the jigsaw graph for every
     * (1 + locationlessPosts.length / 15) markers, see https://www.wolframalpha.com/input/?i=y+%3D+%28x+mod+5%29
     * We want to add a new row for every 15 markers, therefore we increase the divisor every 15 marker
     * (hence locationlessPosts.length / 15))
     *
     * * Longitude from -180 -> 180:
     *   Distribute post longitudes equidistantly starting from -180 to 180
     *   With the marker index i in the total of len markers:
     *    - Total number of gaps between posts will be (len + 1): len - 1 gaps in between and 2 gaps on the 2 sides.
     *    - The marker of post i will have the offset of (i+1) gaps to the left of the marker.
     *  At len = 1, the single post should be at lng 0 = 1/2 of 360 (starting from -180)
     *  At len = 2, the two post should be at the 1/3 and 2/3 of 360 (starting from -180)
     *    -> lng = [-60, 120]. Equidistant from the International Date Line and from each other.
     *  At len = 3 post should be at the 1/4, 2/4 and 3/4 of 360 (starting from -180)
     *    -> lng = [-90, 0, 90]. Equidistant from the International Date Line and from each other.
     *  At i = x, len = y, post should be at (x+1)/(y+1) of 360 (starting from -180)
     *   Hence the equation lng = -180 + 360(i+1)/(len+1)
     */
    const NUMBER_OF_LOCATIONLESS_POSTS = locationlessPosts.length
    const LOWEST_LATITUDE = -82.5 - NUMBER_OF_LOCATIONLESS_POSTS / 15
    const locationlessPostsLocations = new Map(
      locationlessPosts.map((postCid, index) => [
        postCid,
        {
          // 82.5
          lat: LOWEST_LATITUDE + 3 * (index % (1 + NUMBER_OF_LOCATIONLESS_POSTS / 15)),
          lng: -180 + ((index + 1) * 360) / (NUMBER_OF_LOCATIONLESS_POSTS + 1),
        },
      ]),
    )

    return currentPosts.value.map((p: Post) => {
      return {
        ...p,
        location_point: {
          longitude: p.location_point?.longitude || locationlessPostsLocations.get(p.cid)?.lng || NARNIA_LONGITUDE,
          latitude: p.location_point?.latitude || locationlessPostsLocations.get(p.cid)?.lat || NARNIA_LATITUDE,
        },
        location_name: p.location_name || __('No location'),
      }
    })
  },
)

const postZIndices = computed((): Record<Cid, number> => {
  const zIndices = {}
  currentPosts.value.forEach((post, index, array) => {
    zIndices[post.cid] = 200 + array.length - index
  })
  return zIndices
})

const markers = computed((): PostMarkerObject[] => {
  if (!hasFetchedPosts.value || isSubmissionRequest.value) {
    return []
  }
  return postsWithRenderingLocations.value.map((p) => {
    return {
      id: p.cid,
      position: {
        lng: p.location_point.longitude,
        lat: p.location_point.latitude,
      },
      post: p,
      zIndex: postZIndices.value[p.cid],
    }
  })
})

const mapConnections = computed((): PostConnectionObject[] => {
  return connections.value
    .map((c): { id: Id; from: Post; to: Post } | null => {
      const fromPost = postsWithRenderingLocations.value.find((p) => p.id === c.from_wish_id)
      const toPost = postsWithRenderingLocations.value.find((p) => p.id === c.to_wish_id)
      if (!fromPost) return null
      if (!toPost) return null
      return {
        ...c,
        from: fromPost,
        to: toPost,
      }
    })
    .filter((x) => !!x) as PostConnectionObject[]
})

const latitude = ref(0)
const longitude = ref(0)
const zoom = ref(2)

const zoomToCurrentCountryIfNoPosts = () => {
  // On a map that's very popular, this might be called before posts are fetched and wastefully make a geocoding request
  // => wait for posts to be fetched for a while first
  const startTime = Date.now()
  // have a slightly high timeout here since I assume if posts are not fetched people cannot interact with the page anyways => we can wait for a bit longer
  const waitForPostsTimeoutMs = 5000
  const waitForPosts = new Promise((resolve) => {
    const checkPosts = () => {
      if (hasFetchedPosts.value || currentPosts.value.length > 0 || Date.now() - startTime > waitForPostsTimeoutMs) {
        resolve(true)
      } else {
        setTimeout(checkPosts, 100)
      }
    }
    checkPosts()
  })

  waitForPosts.then(() => {
    if (currentPosts.value.length === 0 && currentCountry.value != null) {
      // Use geocoding service to get the lat, lng and zoomLevel for the country
      // (based on user IP) for setting the starting position
      // Only do this if there is no post (to reduce number of geocoding requests)
      const geocoder = new Geocoder()
      geocoder.geocode(
        {
          address: currentCountry.value,
        } /* can also use `city_name`, only return country_name of geodata from the backend for now */,
        (results, status) => {
          if (status === GeocoderStatus.OK && results && results[0]?.geometry?.location) {
            latitude.value = results[0].geometry.location.lat()
            longitude.value = results[0].geometry.location.lng()
            if (map.value != null && results[0].geometry.viewport) {
              const mapDiv = map.value.getDiv()
              const mapDim = {
                height: mapDiv.clientHeight,
                width: mapDiv.clientWidth,
              }
              zoom.value = getBoundsZoomLevel(results[0].geometry.viewport, mapDim)
            }
          }
        },
      )
    }
  })
}

const allowPreviewPanel = computed<boolean>(() => currentPosts.value.length > 0)

const isMobilePostListOverflowing = ref(false)

// When the number of posts changes, or the post list is toggled,
// or the viewport changes, we re-calculate if the post list will overflow or not.
watch([currentPosts, isPreviewPanelShown, isMobileLayout], () => {
  nextTick(() => {
    if (isMobileLayout.value) {
      const postList = document.getElementById('surface-map-post-list')
      isMobilePostListOverflowing.value = postList ? postList.scrollWidth > postList.clientWidth : false
    } else {
      isMobilePostListOverflowing.value = false
    }
  })
})

watch(mostRecentlyChangedGeolocationPostCid, (cid) => {
  nextTick(() => {
    const post = cid != null ? postEntitiesByCid.value[cid] : null
    if (
      !!map.value &&
      post?.location_point?.longitude !== null &&
      post?.location_point?.longitude !== undefined &&
      post?.location_point?.latitude !== null &&
      post?.location_point?.latitude !== undefined
    ) {
      map.value.panTo({ lat: post.location_point.latitude, lng: post.location_point.longitude })
    }
  })
  if (cid != null) {
    resetMostRecentlyChangedGeolocationPostCid()
  }
})

const isPreviewBeingDragged = ref(false)

const onPreviewItemBeingReordered = () => {
  isPreviewBeingDragged.value = true
}

const onPreviewItemStopBeingReordered = () => {
  isPreviewBeingDragged.value = false
}

let enableDropToPost: ReturnType<typeof useDragAndDropToCreatePost>['enableDropToPost'] | null = null
let disableDropToPost: ReturnType<typeof useDragAndDropToCreatePost>['disableDropToPost'] | null = null

const decideDropToPostBehavior = () => {
  // If preview item is dragged, we disable drop to post
  if (isPreviewBeingDragged.value) {
    disableDropToPost?.()
  }
  // If there is a post being edited, drop will upload file to that post
  else if (postBeingEditedCid.value != null) {
    disableDropToPost?.()
    enableDropToPost?.((post: NewPost) => {
      if (!post || !post.attributes) {
        return
      }
      if (post.attributes.file) {
        uploadFileToPickerBox(post.attributes.file)
      }
      if (post.attributes.attachment) {
        hideContentPickerPanel()
        saveContent({
          content: {
            link: post.attributes.attachment,
          },
        })
      }
    })
  }
  // If not, drops will create post
  else {
    disableDropToPost?.()
    enableDropToPost?.((post: NewPost) => {
      enableMapFittingMarkersBounds()
      createPostImmediately(post)
    })
  }
}

watch([isPreviewBeingDragged, postBeingEditedCid], () => {
  decideDropToPostBehavior()
})

const rootEl = ref<HTMLElement>()

onMounted(() => {
  if (rootEl.value != null) {
    const dropToPost = useDragAndDropToCreatePost(rootEl.value)
    enableDropToPost = dropToPost.enableDropToPost
    disableDropToPost = dropToPost.disableDropToPost
    if (canIPost.value) {
      enableDropToPost()
    } else {
      disableDropToPost()
    }
  }

  enableMapFittingMarkersBounds()
  zoomToCurrentCountryIfNoPosts()

  if (isSubmissionRequest.value) {
    startNewSubmissionRequest()
  }
})
</script>

<template>
  <section
    ref="rootEl"
    :class="['relative', 'surface-container-map', isMobileLayout && !isEmbedded && !isSubmissionRequest && 'v3']"
    :style="isDesktopLayout ? { overflow: 'unset' } : undefined"
  >
    <!-- Use v-if instead of v-show to completely hide SurfaceHeader in desktop layout,
         otherwise there would be two elements with id="surface-header" (SurfaceHeader and SurfaceMapHeaderAndPostListPanel) which breaks functionality -->
    <!-- Use v-show for isPlacingPin since it's a frequently changing condition -->
    <SurfaceHeader v-if="xHeader && (!isDesktopLayout || isEmbedded)" v-show="!isPlacingPin" />
    <div
      v-if="!device.app && !isReadOnly && !isEmbedded && !isMobileLayout && !isSubmissionRequest"
      class="absolute z-search-bar top-4 end-4 flex items-center h-10"
    >
      <SurfaceSearchBar />
      <OzContainedButton
        v-if="isAppUsing('signUpSurface') && !amIRegistered"
        :class="[
          'ms-4',

          // Base
          {
            'bg-[rgba(255,255,255,0.32)]': colorScheme === 'light',
            'bg-[rgba(0,0,0,0.24)]': colorScheme === 'dark',
          },
          'backdrop-blur-sm',
          {
            'text-dark-text-200': colorScheme === 'light',
            'text-light-text-200': colorScheme === 'dark',
          },

          // Hover
          {
            'hover-hover:hover:bg-light-ui-100': colorScheme === 'light',
            'hover-hover:hover:bg-dark-ui-100': colorScheme === 'dark',
          },
          {
            'hover-hover:hover:text-dark-text-100': colorScheme === 'light',
            'hover-hover:hover:text-light-text-100': colorScheme === 'dark',
          },
        ]"
        :href="isNativeTenant ? signupUrl : loginUrl"
        :data-pepin-id="isNativeTenant ? 'signup-cta-on-surface' : 'login-cta-on-surface'"
        :color-scheme="OzContainedButtonColorScheme.Bare"
        :size-preset="OzContainedButtonSizePreset.H32px"
        :dark-mode="colorScheme === 'dark'"
        :text="isNativeTenant ? __('Sign up') : __('Log in')"
      />
    </div>
    <div id="wish-list" :dir="dir()" class="map-content" tabindex="-1">
      <SurfacePadletLogoInScreenshotMode v-if="isScreenshotMode && xLogoInScreenshotMode" class="z-10" />
      <SurfaceMapHeaderAndPostListPanel
        v-if="postListPosition === 'side'"
        :is-shown="isPreviewPanelShown"
        @start-reordering="onPreviewItemBeingReordered"
        @stop-reordering="onPreviewItemStopBeingReordered"
      />
      <SurfaceMapLeaflet
        v-if="isAppUsing('surfaceMapsV2')"
        :latitude="latitude"
        :longitude="longitude"
        :zoom="zoom"
        :markers="markers"
        :theme-id="Number(mapThemeId)"
      />
      <SurfaceMapGoogle
        v-else
        :background-color="backgroundColor"
        :zoom="zoom"
        :latitude="latitude"
        :longitude="longitude"
        :markers="markers"
        :connections="mapConnections"
        :theme-id="Number(mapThemeId)"
        :post-being-edited-cid="postBeingEditedCid"
        :is-mobile-post-list-overflowing="isMobilePostListOverflowing"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        :comment-ids-by-post-id="commentIdsByPostId"
        @initialized="onMapInitialized"
      >
        <template #control>
          <button
            v-if="allowPreviewPanel"
            class="map-button-inverse map-center-map-control"
            :title="__('Open preview panel')"
            @click="toggleMapPreviewPanel"
          >
            <BaseIcon name="toc" />
          </button>
        </template>
      </SurfaceMapGoogle>
      <SurfaceMapPostListHorizontal
        v-if="postListPosition === 'bottom'"
        :class="['absolute z-0 bottom-0 inset-x-0', !isMobilePostListOverflowing && 'mb-2']"
        :is-shown="isPreviewPanelShown"
        :box-color="OzBoxColors.PrimaryBlur"
        @start-reordering="onPreviewItemBeingReordered"
        @stop-reordering="onPreviewItemStopBeingReordered"
      />
    </div>
    <div class="wish-sizer" />
    <!-- Put this component here instead of surface-map-google because components in surface-map-google need to be registered on the map -->
    <SurfaceMapPlacedPinOverlay v-if="isPlacingPin" />
  </section>
</template>

<style lang="scss" scoped>
@import '@assets/stylesheets/3/modules/all';
.wish-sizer {
  width: 254px;
}
.surface-container-map {
  width: 100%;
  // webkit browsers (mainly Safari) doesn't behave well with 100vh (https://css-tricks.com/css-fix-for-100vh-in-mobile-webkit/)
  // we have our own fix for this under bits/fix_mobile_viewport.ts, use it instead of 100vh
  height: var(--vh100);
  &.v3 {
    height: calc(var(--vh100) - 52px);
  }
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}
.map-content {
  width: 100%;
  display: flex;
  flex-grow: 1;
  position: relative;
}
#map {
  height: 100%;
  width: 100%;
  @include respondToSmallscreen {
    margin-bottom: 15vh;
  }
}
</style>
